// =============================================================================
// BP-05-03: Calendar Frontend - AppointmentFormPage
// =============================================================================

import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { AppointmentForm } from '../components';
import { useAppointment } from '../hooks/useAppointments';
import { type Appointment } from '../types';

// -----------------------------------------------------------------------------
// Mock Data (Replace with actual data fetching)
// -----------------------------------------------------------------------------

const mockChildren = [
  { id: '1', name: 'Emma' },
  { id: '2', name: 'Lucas' },
];

const mockCareTeam = [
  { id: '1', name: 'Dr. Sarah Chen', type: 'Neurologist' },
  { id: '2', name: 'Dr. Michael Park', type: 'Physical Therapist' },
  { id: '3', name: 'Lisa Johnson', type: 'Occupational Therapist' },
];

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function AppointmentFormPage() {
  const { familyId = '', appointmentId } = useParams<{
    familyId: string;
    appointmentId?: string;
  }>();
  const navigate = useNavigate();

  const isEditing = !!appointmentId;

  // Fetch appointment if editing
  const { data: appointment, isLoading } = useAppointment(appointmentId || '', {
    enabled: isEditing,
  });

  const handleSuccess = (appointment: Appointment) => {
    navigate(`/family/${familyId}/appointments/${appointment.id}`);
  };

  const handleCancel = () => {
    if (isEditing && appointmentId) {
      navigate(`/family/${familyId}/appointments/${appointmentId}`);
    } else {
      navigate(`/family/${familyId}/calendar`);
    }
  };

  if (isEditing && isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-warm-gray/10 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-cream rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-navy" />
            </button>
            <div>
              <h1 className="text-xl font-display font-semibold text-navy">
                {isEditing ? 'Edit Appointment' : 'New Appointment'}
              </h1>
              <p className="text-sm text-warm-gray">
                {isEditing
                  ? 'Update appointment details'
                  : 'Schedule a new appointment'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-soft p-6">
          <AppointmentForm
            familyId={familyId}
            appointment={appointment}
            children={mockChildren}
            careTeam={mockCareTeam}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
          />
        </div>
      </main>
    </div>
  );
}


export default AppointmentFormPage;
